import * as _ from 'lodash-es';
import Block from 'quill/blots/block';
import type { Delta, EmitterSource } from 'quill/core';
import Quill from 'quill/core';
import type { Range } from 'quill/core/selection';
import { useEffect } from 'react';
import useAppSelector from '../app/redux/useAppSelector';
import {
  ACTION_AI_COMPOSE_ACCEPT,
  ACTION_AI_COMPOSE_SHOW,
  CATEGORY_PRODUCT,
} from '../constants/analytics';
import useIsLayoutSize from '../post/utils/useIsLayoutSize';
import { LayoutSize } from '../post/utils/useLayoutSize';
import { track } from '../utils/analytics';
import { isFeatureEnabled } from '../utils/features';
import type { BindingObject } from './BaseKeyboard';
import { useQuill, useQuillReadOnly } from './QuillContext';
import Header from './formats/Header';
import * as styles from './useAICompose.module.scss';

const { placeholderAttribute } = styles;

const extractText = (delta: Delta) =>
  delta
    .map((op) => {
      if (typeof op.insert === 'string') {
        return op.insert;
      }
      return '\n';
    })
    .join('');

const normalizeSuggestions = (suggestions: string[]) =>
  _.compact(suggestions.map((suggestion) => suggestion.split(/\r|\n/)[0]));

const calculateState = (
  range: Range | null,
  quill: Quill,
  payloads: Payload[],
): State | null => {
  if (!range || range.length > 0) return null;
  const filtered = payloads.filter((payload) => range.index >= payload.index);
  if (!filtered.length) return null;
  const [line, lineOffset] = quill.getLine(range.index);
  if (!(line instanceof Block) || lineOffset < line.length() - 1) {
    return null;
  }

  const lineDelta = line.delta();
  const isEndingWithCode = !!_.nth(lineDelta.ops, -2)?.attributes?.code;
  if (isEndingWithCode) {
    return null;
  }
  const lineText = extractText(lineDelta);

  for (const payload of filtered) {
    const payloadLineOffset = lineOffset - range.index + payload.index;
    if (payloadLineOffset > 0) {
      const prompt = lineText.slice(
        Math.max(0, payloadLineOffset - 10),
        payloadLineOffset,
      );

      if (payload.prompt.endsWith(prompt)) {
        const suffix = lineText.slice(payloadLineOffset, -1);
        const fullSuggestion = payload.suggestions.find(
          (text) => text !== suffix && text.startsWith(suffix),
        );
        if (fullSuggestion) {
          const suggestion = fullSuggestion.slice(suffix.length);
          const firstWhitespaceIndex = suggestion.indexOf(' ', 1);
          return {
            payload,
            suggestion:
              firstWhitespaceIndex === -1
                ? suggestion
                : suggestion.slice(0, firstWhitespaceIndex),
            fullSuggestion,
            hasMoreText: firstWhitespaceIndex !== -1,
            block: line,
          };
        }
      }
    }
  }
  return null;
};

interface Payload {
  index: number;
  suggestions: string[];
  prompt: string;
  version: number;
}

interface State {
  block: Block;
  payload: Payload;
  hasMoreText: boolean;
  suggestion: string;
  fullSuggestion: string;
}

const useAICompose = (postId: string) => {
  const { quill } = useQuill();
  const readOnly = useQuillReadOnly();
  const channel = useAppSelector((state) => state.editor.channel);

  const isMobile = useIsLayoutSize(LayoutSize.mobile);

  useEffect(() => {
    if (!channel || !quill || readOnly || isMobile) return undefined;

    let payloads: Payload[] = [];
    let state: State | null = null;

    const setState = (newState: State | null) => {
      if (state) {
        quill.root
          .querySelectorAll(`[${placeholderAttribute}]`)
          .forEach((node) => {
            if (newState?.block.domNode !== node) {
              node.removeAttribute(placeholderAttribute);
            }
          });
      }

      if (newState) {
        newState.block.domNode.setAttribute(
          placeholderAttribute,
          newState.suggestion,
        );

        const isSuggestingNewWord = !state?.suggestion.endsWith(
          newState.suggestion,
        );
        if (isSuggestingNewWord) {
          track(ACTION_AI_COMPOSE_SHOW, {
            category: CATEGORY_PRODUCT,
            label: postId,
            suggestion: newState.suggestion,
            full_suggestion: newState.fullSuggestion,
          });
        }
      }
      state = newState;
    };

    const PREFIX_TRIGGER_REGEX = /(^|[^\w])[:/@]/;
    const MARKDOWN_SHORTCUT_REGEX = /[*\-#`>]$/;
    const shouldIgnorePayload = (prompt: string) => {
      const lastSentence = prompt.split(/\n|[.,!?]\s/).pop();
      if (
        lastSentence &&
        (PREFIX_TRIGGER_REGEX.test(lastSentence) ||
          MARKDOWN_SHORTCUT_REGEX.test(lastSentence))
      ) {
        return true;
      }
      return false;
    };

    const ref = channel.on('ai_predict', (payload: Payload) => {
      if (state && state.payload.version > payload.version) return;
      if (shouldIgnorePayload(payload.prompt)) return;
      const suggestions = normalizeSuggestions(payload.suggestions);
      payloads = _.compact([state?.payload, { ...payload, suggestions }]);

      const range = quill.getSelection();
      setState(calculateState(range, quill, payloads));

      if (!isFeatureEnabled('devtools')) return;
      /* eslint-disable no-console */
      console.group(
        `ai_predict at index ${payload.index}, version ${payload.version}, prompt "${payload.prompt}"`,
      );
      suggestions.forEach((suggestion) => console.log(suggestion));
      console.groupEnd();
      /* eslint-enable no-console */
    });

    const bindings: BindingObject[] = [
      {
        key: ['Tab', 'ArrowRight'],
        collapsed: true,
        handler: (range) => {
          if (!state) return true;

          const { suggestion, fullSuggestion, block, hasMoreText } = state;

          const textToComplete =
            hasMoreText &&
            !(block instanceof Header || block === quill.titleLine)
              ? `${suggestion} `
              : suggestion;

          quill.history.cutoff();
          quill.insertText(range.index, textToComplete, Quill.sources.USER);
          quill.setSelection(
            range.index + textToComplete.length,
            Quill.sources.SILENT,
          );
          setState(
            calculateState(
              { index: range.index + textToComplete.length, length: 0 },
              quill,
              payloads,
            ),
          );

          track(ACTION_AI_COMPOSE_ACCEPT, {
            category: CATEGORY_PRODUCT,
            label: postId,
            suggestion,
            full_suggestion: fullSuggestion,
          });

          return false;
        },
      },
      {
        key: ['Escape'],
        collapsed: true,
        handler: () => {
          if (!state) return true;

          payloads = [];
          setState(null);
          return false;
        },
      },
    ];
    bindings.forEach((binding) => {
      // @ts-expect-error
      quill.keyboard.prependBinding(binding);
    });

    const editorChangeHandler = (type: string, ...args: unknown[]) => {
      const source = args[2] as EmitterSource;

      if (type === Quill.events.SELECTION_CHANGE) {
        const range = args[0] as Range | null;
        setState(calculateState(range, quill, payloads));
      } else if (type === Quill.events.TEXT_CHANGE) {
        const delta = args[0] as Delta;
        if (source !== Quill.sources.USER) {
          payloads = payloads.map((payload) => ({
            ...payload,
            index: delta.transform(payload.index),
          }));
        }
      }
    };
    quill.on(Quill.events.EDITOR_CHANGE, editorChangeHandler);

    return () => {
      channel.off('ai_predict', ref);
      bindings.forEach((binding) => {
        // @ts-expect-error
        quill.keyboard.removeBinding(binding);
      });
      quill.off(Quill.events.EDITOR_CHANGE, editorChangeHandler);
      if (state) {
        quill.root
          .querySelectorAll(`[${placeholderAttribute}]`)
          .forEach((node) => {
            node.removeAttribute(placeholderAttribute);
          });
      }
    };
  }, [quill, channel, readOnly, isMobile, postId]);
};

export default useAICompose;
